/**
 * The report's metric identity — one implementation, two consumers.
 *
 * The report renders a metric row per `(source, cadence)` pair, and the
 * exec-summary service builds the LLM's `{{metric:<id>:…}}` token allowlist
 * from the *same* pairs. Those ids and labels are a contract between the two:
 * the browser resolves a stored `{kind:"metric", id}` ref against whatever the
 * renderer produced, so an id the API offers but the renderer never emits
 * degrades the bullet to `missing` and deletes it from the summary.
 *
 * They used to be two hand-written copies, and they had already drifted. This
 * module is the single source; `apps/web/lib/compass/report.ts` is the
 * source of truth for the *rules* it encodes, because it is what the reader
 * actually sees.
 */

export interface MetricGroupMetric {
  id: string;
  label: string;
  description?: string | null;
}

export interface MetricGroupStream {
  name: string;
  direction: "benefit" | "cost";
  metricId?: string | null;
  cadence?: string | null;
  description?: string | null;
  amount?: number | null;
}

export type ReportCadence = "annual" | "one-time" | "one-time-with-rollout";

export const REPORT_CADENCES: readonly ReportCadence[] = [
  "annual",
  "one-time",
  "one-time-with-rollout",
];

/**
 * Explicit one-time and rollout-release streams retain their own report
 * cadence; missing or unknown legacy cadence remains annual.
 */
export function valueStreamCadence(stream: MetricGroupStream): ReportCadence {
  if (stream.cadence === "one-time-with-rollout")
    return "one-time-with-rollout";
  return stream.cadence === "one-time" ? "one-time" : "annual";
}

export interface ValueStreamBuckets<
  M extends MetricGroupMetric,
  S extends MetricGroupStream,
> {
  metrics: M[];
  byMetricId: Map<string, S[]>;
  otherBenefits: S[];
  costs: S[];
}

export interface MetricGroupSource<
  M extends MetricGroupMetric,
  S extends MetricGroupStream,
> {
  metrics?: readonly M[] | null;
  valueStreams: readonly S[];
}

function dedupeMetrics<M extends MetricGroupMetric>(
  metrics: readonly M[],
): M[] {
  const seen = new Set<string>();
  return metrics.filter((metric) => {
    if (seen.has(metric.id)) return false;
    seen.add(metric.id);
    return true;
  });
}

export function bucketValueStreams<
  M extends MetricGroupMetric,
  S extends MetricGroupStream,
>(view: MetricGroupSource<M, S>): ValueStreamBuckets<M, S> {
  const metrics = dedupeMetrics(view.metrics ?? []);
  const byMetricId = new Map(metrics.map((metric) => [metric.id, [] as S[]]));
  const otherBenefits: S[] = [];
  const costs: S[] = [];

  for (const stream of view.valueStreams) {
    if (stream.direction === "cost") {
      costs.push(stream);
      continue;
    }
    const metricStreams = stream.metricId
      ? byMetricId.get(stream.metricId)
      : undefined;
    if (metricStreams) metricStreams.push(stream);
    else otherBenefits.push(stream);
  }

  return { metrics, byMetricId, otherBenefits, costs };
}

/**
 * What a metric card is actually reporting.
 *
 * The group is keyed by *outcome metric* — "Prior authorization denial rate",
 * a percentage — while the amount beside it is the money of the value streams
 * attached to that metric. Naming the card after the metric produced cards
 * that contradicted themselves: a title reading "…denial rate", a value
 * reading "$10.92M per year", and a description opening "The percentage of…".
 *
 * A single-stream group is unambiguous — the stream is the quantity, and it is
 * already named for it ("Avoided prior-authorization denial value") — so it
 * takes the stream's own name and description. Groups that roll several
 * streams together keep the metric's, because no one stream describes them.
 *
 * A stream name is only better than the metric's when it is a *name*. Model
 * generation also emits stream names as snake_case slugs
 * (`avoided_inpatient_nursing_overtime_cost` beside the metric's perfectly good
 * "Annual Avoided Inpatient Nursing Overtime Cost"), and no reader-facing string
 * may carry an identifier.
 */
export function isSlugLikeName(name: string): boolean {
  const trimmed = name.trim();
  if (!trimmed) return true;
  // An underscore never appears in prose; a hyphen only reads as a separator
  // when there is no whitespace at all ("avoided-overtime-cost", but not
  // "Avoided overtime — nurse-led units").
  return (
    trimmed.includes("_") || (!/\s/.test(trimmed) && trimmed.includes("-"))
  );
}

/**
 * Exact group total behind the report's formatted amount. `null` when any
 * contributing stream has no amount — a partial sum would understate.
 */
export function metricGroupAmount(
  streams: readonly MetricGroupStream[],
): number | null {
  if (streams.length === 0) return null;
  let total = 0;
  for (const stream of streams) {
    if (stream.amount == null) return null;
    total += Math.abs(stream.amount);
  }
  return total;
}

export interface MetricGroup<S extends MetricGroupStream = MetricGroupStream> {
  id: string;
  /** Reader-facing label, identity already resolved — see `isSlugLikeName`. */
  label: string;
  description: string | null;
  cadence: ReportCadence;
  /**
   * Benefit groups are the report's Metrics; cost groups exist only so their
   * streams reach `traceNodes` and the `totalCost` figure's contributors.
   */
  direction: "benefit" | "cost";
  streams: S[];
}

function groupIdentity<S extends MetricGroupStream>(
  fallbackLabel: string,
  fallbackDescription: string | null,
  streams: readonly S[],
): { label: string; description: string | null } {
  const only = streams.length === 1 ? streams[0] : undefined;
  // Identity is taken wholesale from one side or the other: pairing the metric's
  // label with the stream's description is how the self-contradicting card this
  // function exists to prevent got written in the first place.
  if (!only || isSlugLikeName(only.name)) {
    return { label: fallbackLabel, description: fallbackDescription };
  }
  return { label: only.name, description: only.description ?? null };
}

/**
 * One group per (source, cadence) pair that actually has streams.
 *
 * Splitting on cadence is what keeps `ReportMetricRow.cadence` honest:
 * `ReportMetricsList` files each row under an "Annual", "One-time", or
 * "Rollout release"
 * subhead by that field alone, so an annual row must never carry a one-time
 * stream. Cost streams carry `metricId: null` in the read model, so they
 * group by cadence rather than by metric (plan §9 #9) and take the design's
 * own two labels.
 */
export function buildMetricGroups<
  M extends MetricGroupMetric,
  S extends MetricGroupStream,
>(view: MetricGroupSource<M, S>): MetricGroup<S>[] {
  const buckets = bucketValueStreams(view);
  const groups: MetricGroup<S>[] = [];

  const addByCadence = (
    idBase: string,
    fallbackLabel: string,
    fallbackDescription: string | null,
    direction: "benefit" | "cost",
    streams: readonly S[],
  ) => {
    for (const cadence of REPORT_CADENCES) {
      const matching = streams.filter(
        (stream) => valueStreamCadence(stream) === cadence,
      );
      if (matching.length === 0) continue;
      groups.push({
        id: cadence === "annual" ? idBase : `${idBase}:${cadence}`,
        ...groupIdentity(fallbackLabel, fallbackDescription, matching),
        cadence,
        direction,
        streams: matching,
      });
    }
  };

  for (const metric of buckets.metrics) {
    const streams = buckets.byMetricId.get(metric.id) ?? [];
    // A metric with no active stream has no amount to report. It is surfaced
    // on the assumptions page as an "unmodelled metric"; rendering it here
    // with `—` would read as "not computed yet", which is a different thing.
    if (streams.length === 0) continue;
    addByCadence(
      metric.id,
      metric.label,
      metric.description ?? null,
      "benefit",
      streams,
    );
  }

  // Benefits whose `metricId` is absent or unknown to the view. Grouped so
  // they cannot silently vanish from the report.
  addByCadence(
    "other-benefits",
    "Other benefits",
    null,
    "benefit",
    buckets.otherBenefits,
  );

  for (const cadence of REPORT_CADENCES) {
    const costs = buckets.costs.filter(
      (stream) => valueStreamCadence(stream) === cadence,
    );
    if (costs.length === 0) continue;
    groups.push({
      id: `cost-streams:${cadence}`,
      label:
        cadence === "annual"
          ? "Program cost"
          : cadence === "one-time"
            ? "Implementation"
            : "Rollout release",
      description: null,
      cadence,
      direction: "cost",
      streams: costs,
    });
  }

  return groups;
}

export interface ReportMetricIdentity {
  id: string;
  label: string;
  cadence: ReportCadence;
  /** `null` when a contributing stream has no amount yet. */
  amount: number | null;
}

/**
 * The metric rows a reader can actually see, in render order.
 *
 * Benefits only, deliberately. `ReportData.metrics` — the collection
 * `resolveReportProse` resolves a `{kind:"metric", id}` ref against — filters
 * to `direction === "benefit"`, because a cost group rendered as a metric row
 * is a tile in the same grid, in the same type, with no sign, inviting a
 * reader to add it to the benefits beside it. Program cost is the Figures
 * section's job.
 *
 * The consequence for the exec summary is that `cost-streams:annual` and
 * `cost-streams:one-time` must **never** reach the token allowlist: the LLM
 * would happily reference one, and the ref would resolve to `missing` at
 * render and drop the whole bullet. Withholding them is the whole point of
 * this projection.
 */
export function reportMetricIdentities<
  M extends MetricGroupMetric,
  S extends MetricGroupStream,
>(view: MetricGroupSource<M, S>): ReportMetricIdentity[] {
  return buildMetricGroups(view)
    .filter((group) => group.direction === "benefit")
    .map((group) => ({
      id: group.id,
      label: group.label,
      cadence: group.cadence,
      amount: metricGroupAmount(group.streams),
    }));
}
